/**
 * Keeps the screen on while someone is looking at it from afar: a phone that locks during a session
 * suspends the app, and the other side sees a frozen screen. The browser drops the lock whenever the
 * page is hidden, so it is taken again each time the page comes back.
 */
export class ScreenWakeLock {
    private sentinel: WakeLockSentinel | null = null;
    private isWanted = false;

    acquire(): void {
        if (this.isWanted) {
            return;
        }

        this.isWanted = true;
        document.addEventListener('visibilitychange', this.reacquire);
        this.request();
    }

    release(): void {
        this.isWanted = false;
        document.removeEventListener('visibilitychange', this.reacquire);
        this.sentinel?.release().catch(() => undefined);
        this.sentinel = null;
    }

    private readonly reacquire = (): void => {
        if (document.visibilityState === 'visible') {
            this.request();
        }
    };

    private async request(): Promise<void> {
        if (!this.isWanted || !('wakeLock' in navigator) || document.visibilityState !== 'visible') {
            return;
        }

        try {
            this.sentinel = await navigator.wakeLock.request('screen');
        } catch {
            // Refused (battery saver, unsupported context): the session still works, the screen may lock.
        }
    }
}
